import {
    EntityProfileIndustry,
    EntityProfileRevenueBracket,
    EntityProfileType,
    BusinessTaxReturns,
    FirmSize,
} from "@common/types/entity";
import { OrganizationServicesOffered } from "@common/types/organization";
import {
    BudgetType,
    MeetingOption,
    OrganizationStageOfBusiness,
    ProjectTimeline,
} from "@common/schemas/organization.schema";

export const industryLabels = {
    [EntityProfileIndustry.ACCOUNTANT]: "Accountant",
    [EntityProfileIndustry.AGENCY]: "Agency",
    [EntityProfileIndustry.BOOKKEEPER]: "Bookkeeper",
    [EntityProfileIndustry.CONSTRUCTION]: "Construction",
    [EntityProfileIndustry.CONSULTING]: "Consulting/Services",
    [EntityProfileIndustry.CONTENT]: "Content",
    [EntityProfileIndustry.CRYPTO]: "Crypto",
    [EntityProfileIndustry.TRUCKING]: "Driver/Trucking",
    [EntityProfileIndustry.FINANCIAL_ADVISOR]: "Financial Advisor",
    [EntityProfileIndustry.INVESTOR]: "Investor",
    [EntityProfileIndustry.LEGAL]: "Legal",
    [EntityProfileIndustry.MANUFACTURING]: "Manufacturing",
    [EntityProfileIndustry.MEDICAL]: "Medical",
    [EntityProfileIndustry.NON_PROFIT]: "Non-Profit",
    [EntityProfileIndustry.PHYSICAL_GOODS]: "Physical Goods",
    [EntityProfileIndustry.PROPERTY_MANAGEMENT]: "Property Management",
    [EntityProfileIndustry.PSYCHOLOGIST]: "Psychologist/Therapist",
    [EntityProfileIndustry.REAL_ESTATE_AGENT]: "Real Estate Agent",
    [EntityProfileIndustry.REAL_ESTATE_INVESTOR]: "Real Estate Investor",
    [EntityProfileIndustry.RETAIL]: "Retail",
    [EntityProfileIndustry.SAAS]: "SaaS",
    [EntityProfileIndustry.TRADEPERSON]:
        "Tradesperson (HVAC, Electrician, Plumber)",
    [EntityProfileIndustry.ENTERTAINMENT]: "TV/Film/Entertainment",
    [EntityProfileIndustry.RESTAURANT]: "Restaurant",
    [EntityProfileIndustry.OTHER]: "Other",
} as const satisfies Record<EntityProfileIndustry, string>;

export const firmSizeLabels = {
    [FirmSize["1_19"]]: "1 - 19",
    [FirmSize["20_49"]]: "20 - 49",
    [FirmSize["50_99"]]: "50 - 99",
    [FirmSize["100_199"]]: "100 - 199",
    [FirmSize["200_499"]]: "200 - 499",
    [FirmSize["500_PLUS"]]: "500+",
} as const satisfies Record<FirmSize, string>;

export const businessTaxReturnsLabels = {
    [BusinessTaxReturns["0_249"]]: "0 - 249",
    [BusinessTaxReturns["250_499"]]: "250 - 499",
    [BusinessTaxReturns["500_999"]]: "500 - 999",
    [BusinessTaxReturns["1000_2499"]]: "1000 - 2499",
    [BusinessTaxReturns["2500_PLUS"]]: "2500+",
} as const satisfies Record<BusinessTaxReturns, string>;

export const servicesOfferedLabels = {
    [OrganizationServicesOffered.BOOKKEEPING]: "Bookkeeping",
    [OrganizationServicesOffered.TAXES]: "Taxes",
    [OrganizationServicesOffered.ADVISORY]: "Advisory",
} as const satisfies Record<OrganizationServicesOffered, string>;

export const SERVICE_OPTIONS = Object.values(OrganizationServicesOffered).map(
    (service) => ({ value: service, label: servicesOfferedLabels[service] }),
);

export const entityProfileTypeLabels = {
    [EntityProfileType.C_CORP]: "C Corporation",
    [EntityProfileType.SMLLC]: "LLC (single owner)",
    [EntityProfileType.MMLLC]: "LLC (multiple owners)",
    [EntityProfileType.NO_LEGAL_ENTITY]: "No legal entity",
    [EntityProfileType.PARTNERSHIP]: "Partnership",
    [EntityProfileType.S_CORP]: "S Corporation",
    [EntityProfileType.SOLE_PROPRIETORSHIP]: "Sole Proprietorship",
} as const satisfies Record<EntityProfileType, string>;

export const revenueBracketLabels = {
    [EntityProfileRevenueBracket.LESS_THAN_50K]: "Less than $50k",
    [EntityProfileRevenueBracket.BETWEEN_50K_AND_200K]: "$50k – $200k",
    [EntityProfileRevenueBracket.BETWEEN_200K_AND_1M]: "$200k – $1M",
    [EntityProfileRevenueBracket.MORE_THAN_1M]: "$1M+",
} as const satisfies Record<EntityProfileRevenueBracket, string>;

export const stageOfBusinessLabels = {
    [OrganizationStageOfBusiness.EARLY]: "Early stage",
    [OrganizationStageOfBusiness.ESTABLISHED]: "Established",
    [OrganizationStageOfBusiness.GROWING]: "Growing",
    [OrganizationStageOfBusiness.VENTURE_BACKED]: "Venture backed",
} as const satisfies Record<OrganizationStageOfBusiness, string>;

export const projectTimelineLabels = {
    [ProjectTimeline.ONE_TIME]: "One time",
    [ProjectTimeline.ONGOING_SUPPORT]: "Ongoing support",
    [ProjectTimeline.UNSURE]: "Unsure",
} as const satisfies Record<ProjectTimeline, string>;

export const budgetTypeLabels = {
    [BudgetType.LESS_THAN_500]: "Less than $500",
    [BudgetType.BETWEEN_500_AND_1000]: "$500 – $1,000",
    [BudgetType.BETWEEN_1000_AND_2000]: "$1,000 – $2,000",
    [BudgetType.MORE_THAN_2000]: "$2,000+",
} as const satisfies Record<BudgetType, string>;

export const meetingOptionLabels = {
    [MeetingOption.IN_PERSON]: "In person",
    [MeetingOption.VIRTUAL]: "Virtual",
    [MeetingOption.HYBRID]: "Hybrid",
    [MeetingOption.NO_PREFERENCE]: "No preference",
} as const satisfies Record<MeetingOption, string>;
